import React, { useCallback, useEffect, useState } from 'react';
import { Button, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { ArrowClockwise20Regular, ArrowUndo16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';
import { ImportJobProgressBar } from '../ProviderConsumptionImport/ImportJobProgress';
import { formatDateTime } from '../../../functions/ProviderConsumptions.Functions';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  importJobErrorText,
  importJobStatusLabelKey,
  importJobStatusTone,
  isImportJobActive,
  isImportJobFailed,
  isImportJobSucceeded,
} from '../../../functions/ImportJobs.Functions';
import { ProviderImportJob } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';

export interface ImportJobsHistoryDrawerProps {
  open: boolean;
  onDismiss: () => void;
  providerName: string;
  /** GET protocols/providers/{int}/consumptions/import-jobs?take=20 (más nuevos primero). */
  LoadJobs: () => Promise<ProviderImportJob[]>;
  /** "Ver consumos" de un job terminado bien (filtra la grilla por su archivo). */
  onViewConsumptions?: (job: ProviderImportJob) => void;
  /** "Reintentar" de un job fallido (abre el asistente con su archivo). */
  onRetry?: (job: ProviderImportJob) => void;
  /** "Deshacer importación" de un job terminado bien: deshace su archivo (spec 010, US7). Sin callback no se ofrece. */
  onUndoImport?: (job: ProviderImportJob) => void;
}

/** "Historial de importaciones" del proveedor (spec 008): estado, fechas, resultado y error de cada job. */
export const ImportJobsHistoryDrawer = (props: ImportJobsHistoryDrawerProps) => {
  const { t } = useTranslation();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.jobs.history.${path}`, options);
  const [jobs, setJobs] = useState<ProviderImportJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      setJobs(await props.LoadJobs());
    } catch (failure: unknown) {
      setError(failure);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.open) load();
  }, [props.open, load]);

  const renderResult = (job: ProviderImportJob) => {
    if (isImportJobActive(job)) return <ImportJobProgressBar job={job} size="small" data-testid={`pcs-job-progress-${job.id}`} />;
    if (isImportJobFailed(job)) {
      return (
        <div className="pcs-job-cell">
          <span className="pcs-job-error" data-testid={`pcs-job-error-${job.id}`}>
            {importJobErrorText(job) || t('providerConsumptionImport.jobs.failedText')}
          </span>
          {props.onRetry && (
            <GuideLinkButton size="small" onClick={() => props.onRetry?.(job)} data-testid={`pcs-job-retry-${job.id}`}>
              {t('providerConsumptionImport.jobs.retry')}
            </GuideLinkButton>
          )}
        </div>
      );
    }
    if (isImportJobSucceeded(job)) {
      const result = job.result;
      return (
        <div className="pcs-job-cell">
          <span className="ichtys-num" data-testid={`pcs-job-result-${job.id}`}>
            {tr('resultValue', {
              created: result?.created ?? 0,
              total: result?.totalRows ?? 0,
              skipped: result?.skippedExisting ?? 0,
              services: result?.medicalServicesCreated ?? 0,
            })}
          </span>
          {(result?.warnings?.length ?? 0) > 0 && <span className="pcs-secondary">{tr('warnings', { count: result?.warnings?.length })}</span>}
          <div className="pcs-job-links">
            {props.onViewConsumptions && (
              <GuideLinkButton size="small" onClick={() => props.onViewConsumptions?.(job)} data-testid={`pcs-job-view-${job.id}`}>
                {t('providerConsumptionImport.jobs.notice.viewConsumptions')}
              </GuideLinkButton>
            )}
            {props.onUndoImport && (
              // Se deshace el archivo (todas sus importaciones; el diálogo lo advierte): no hay deshacer por corrida (spec 010, US7).
              <GuideLinkButton
                size="small"
                icon={<ArrowUndo16Regular />}
                onClick={() => props.onUndoImport?.(job)}
                aria-label={t('providerConsumptionImport.undo.actionFile', { name: job.fileName || '—' })}
                data-testid={`pcs-job-undo-${job.id}`}
              >
                {t('providerConsumptionImport.undo.action')}
              </GuideLinkButton>
            )}
          </div>
        </div>
      );
    }
    return <span className="pcs-muted">—</span>;
  };

  const renderBody = () => {
    if (loading && jobs.length === 0) return <GuideSkeleton rows={4} label={tr('loading')} data-testid="pcs-jobs-loading" />;
    if (error !== undefined) return null;
    if (jobs.length === 0) return <GuideEmptyState title={tr('emptyTitle')} text={tr('emptyText')} data-testid="pcs-jobs-empty" />;
    return (
      <GuideTable ariaLabel={tr('title')} data-testid="pcs-jobs-table" scrollTestId="pcs-jobs-table-scroll">
        <thead>
          <tr>
            <GuideTh>{tr('columns.file')}</GuideTh>
            <GuideTh>{tr('columns.status')}</GuideTh>
            <GuideTh>{tr('columns.requestedAt')}</GuideTh>
            <GuideTh>{tr('columns.finishedAt')}</GuideTh>
            <GuideTh>{tr('columns.result')}</GuideTh>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.id} className={guideRowClassName({ pending: isImportJobFailed(job) })} data-testid={`pcs-job-${job.id}`}>
              <td>
                <span className="pci-strong">{job.fileName || '—'}</span>
              </td>
              <td>
                <StatusPill tone={importJobStatusTone(job)} data-testid={`pcs-job-status-${job.id}`}>
                  {t(importJobStatusLabelKey(job))}
                </StatusPill>
              </td>
              <td className="pcs-nowrap">
                <span className="ichtys-num">{formatDateTime(job.requestedAt)}</span>
                {job.requestedByUserName && <span className="pcs-secondary">{job.requestedByUserName}</span>}
              </td>
              <td className={cell('num')}>{isImportJobActive(job) ? tr('inProgress') : formatDateTime(job.finishedAt)}</td>
              <td>{renderResult(job)}</td>
            </tr>
          ))}
        </tbody>
      </GuideTable>
    );
  };

  return (
    <GuideDrawer
      open={props.open}
      size="large"
      onDismiss={props.onDismiss}
      eyebrow={tr('eyebrow', { name: props.providerName })}
      title={tr('title')}
      subtitle={tr('subtitle')}
      headerActions={
        <Button appearance="subtle" icon={<ArrowClockwise20Regular />} onClick={load} disabled={loading} data-testid="pcs-jobs-reload">
          {tr('reload')}
        </Button>
      }
      testId="pcs-jobs-drawer"
    >
      {error !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-jobs-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(error)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={load}>{t('providerConsumptions.errors.retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {renderBody()}
    </GuideDrawer>
  );
};

export default ImportJobsHistoryDrawer;
